import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import Editor from '@monaco-editor/react';
import {
  CloudUploadOutlined,
  HistoryOutlined,
  RefreshOutlined,
  SaveOutlined,
} from '@mui/icons-material';
import { fetchData, postData } from '../../helpers/apiUtils';
import { HubSite } from './types';

/**
 * Die JS-Ebenen (2026-09-10) — Basis für alle Portale mit Dateikanal, dazu eine Ebene je Portal.
 *
 * Gespiegelt vom CSS-Teil des Design-Dialogs, mit derselben Zustandsmaschine: solange eine Ebene
 * nicht `ready` ist, wird weder gespeichert noch ausgespielt, und ein Ladefehler bleibt ein Fehler
 * statt eines leeren Felds. Begründung dort (`DesignDialog.tsx`, `LayerStatus`).
 *
 * Unterschiede zu CSS, alle serverseitig durchgesetzt und hier nur sichtbar gemacht:
 *  - eigene Freigabeliste (`js_editor_users`), 403 `js_editor_required`
 *  - Syntaxprüfung beim Speichern, 400 `js_syntax` mit Zeile
 *  - Ausspielen nur auf Portale mit `js_enabled` (seit 2026-09-11 alle sechs; ein NEU angelegtes
 *    Portal startet bei 0, fail-closed)
 */

type Status = 'idle' | 'loading' | 'ready' | 'error';

interface Ebene {
  status: Status;
  js: string;
  saved: string;
  sha: string;
  error: string | null;
}

const LEER: Ebene = { status: 'idle', js: '', saved: '', sha: '', error: null };

interface JsVersion {
  id: number;
  length: number;
  sha256: string;
  change_note: string | null;
  created_by: string | null;
  created_at: string;
}

type Scope = 'base' | 'override';

interface Props {
  open: boolean;
  sites: HubSite[];
}

const hatKanal = (s: HubSite) => Number(s.js_enabled) === 1;

const JsEbenenPanel: React.FC<Props> = ({ open, sites }) => {
  const [scope, setScope] = useState<Scope>('base');
  const [base, setBase] = useState<Ebene>(LEER);
  const [portal, setPortal] = useState<Ebene>(LEER);
  const [siteKey, setSiteKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{
    severity: 'success' | 'info' | 'warning' | 'error';
    text: string;
  } | null>(null);
  const [versions, setVersions] = useState<JsVersion[] | null>(null);
  const [leerFrage, setLeerFrage] = useState<{ scope: Scope; bisher: number } | null>(null);

  const aktiv = scope === 'base' ? base : portal;
  const dirty = aktiv.status === 'ready' && aktiv.js !== aktiv.saved;
  const bereit = aktiv.status === 'ready' && !busy;
  const kanalSites = sites.filter(
    (s) => hatKanal(s) && ['planned', 'live'].includes(s.receiver_status ?? ''),
  );
  const gewaehlt = sites.find((s) => s.site_key === siteKey);

  // ---------------------------------------------------------------- Laden

  const ladeBase = useCallback(async () => {
    setBase((p) => ({ ...p, status: 'loading', error: null }));
    try {
      const r = await fetchData<{ js: string; sha256: string }>('/api/broker-hub/settings/base-js');
      if (!r.success || !r.data || typeof r.data.js !== 'string') {
        throw new Error(r.error || 'Basis-JS konnte nicht geladen werden');
      }
      setBase({ status: 'ready', js: r.data.js, saved: r.data.js, sha: r.data.sha256, error: null });
    } catch (e) {
      setBase({ ...LEER, status: 'error', error: (e as Error).message });
    }
  }, []);

  const ladePortal = useCallback(async (key: string, abgebrochen: () => boolean) => {
    if (!key) {
      setPortal(LEER);
      return;
    }
    setPortal((p) => ({ ...p, status: 'loading', error: null }));
    try {
      const r = await fetchData<{ js: string; sha256: string }>(
        `/api/broker-hub/sites/${key}/override-js`,
      );
      if (abgebrochen()) return;
      if (!r.success || !r.data || typeof r.data.js !== 'string') {
        throw new Error(r.error || `Portal-JS (${key.toUpperCase()}) nicht ladbar`);
      }
      setPortal({ status: 'ready', js: r.data.js, saved: r.data.js, sha: r.data.sha256, error: null });
    } catch (e) {
      if (abgebrochen()) return;
      setPortal({ ...LEER, status: 'error', error: (e as Error).message });
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setMessage(null);
    setVersions(null);
    void ladeBase();
    // Wie im CSS-Teil: vorausgewählt wird hier, geladen vom Effekt darunter — nicht im onChange.
    setSiteKey((vorher) => vorher || sites.find(hatKanal)?.site_key || sites[0]?.site_key || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let tot = false;
    void ladePortal(siteKey, () => tot);
    setVersions(null);
    return () => {
      tot = true;
    };
  }, [open, siteKey, ladePortal]);

  // ---------------------------------------------------------------- Speichern

  const speichern = async (s: Scope, allowEmpty = false) => {
    const ebene = s === 'base' ? base : portal;
    const setEbene = s === 'base' ? setBase : setPortal;
    if (ebene.status !== 'ready') return;
    const url =
      s === 'base' ? '/api/broker-hub/settings/base-js' : `/api/broker-hub/sites/${siteKey}/override-js`;

    setBusy(true);
    setMessage(null);
    const res = await fetchData<{ js: string; sha256: string }>(url, {
      method: 'PUT',
      body: JSON.stringify({ js: ebene.js, expectedSha: ebene.sha, allowEmpty }),
    });
    setBusy(false);

    const code = (res as any)?.code as string | undefined;
    if (code === 'js_empty') {
      setLeerFrage({ scope: s, bisher: (res as any)?.details?.previousLength ?? ebene.saved.length });
      return;
    }
    if (code === 'js_conflict') {
      setMessage({
        severity: 'error',
        text: `${res.error} Deine Eingabe steht noch im Editor — kopiere sie heraus, lade neu und wende sie erneut an.`,
      });
      return;
    }
    if (code === 'js_syntax' || code === 'js_editor_required') {
      setMessage({ severity: 'error', text: res.error || 'Nicht gespeichert.' });
      return;
    }
    if (!res.success || typeof res.data?.js !== 'string' || typeof res.data?.sha256 !== 'string') {
      setMessage({
        severity: 'error',
        text: res.error || 'Speichern fehlgeschlagen — die Antwort des Servers war unvollständig.',
      });
      return;
    }
    setEbene({ status: 'ready', js: res.data.js, saved: res.data.js, sha: res.data.sha256, error: null });
    if (versions !== null) void ladeVersionen(s);
    setMessage({
      severity: 'success',
      text:
        `${s === 'base' ? 'Basis-JS' : `Portal-JS für ${siteKey.toUpperCase()}`} gespeichert ` +
        `(${ebene.saved.length} → ${res.data.js.length} Zeichen). Auf die Seiten kommt es erst mit „Ausspielen".`,
    });
  };

  // ---------------------------------------------------------------- Fassungen

  const ladeVersionen = async (s: Scope) => {
    const key = s === 'base' ? 'base' : siteKey;
    if (!key) return;
    const r = await fetchData<JsVersion[]>(`/api/broker-hub/js-versions/${key}`);
    setVersions(r.success && Array.isArray(r.data) ? r.data : []);
  };

  const wiederherstellen = async (id: number) => {
    setBusy(true);
    setMessage(null);
    const res = await postData<{ length: number }>(`/api/broker-hub/js-version/${id}/restore`, {});
    setBusy(false);
    if (!res.success || !res.data) {
      setMessage({ severity: 'error', text: res.error || 'Wiederherstellen fehlgeschlagen' });
      return;
    }
    if (scope === 'base') await ladeBase();
    else await ladePortal(siteKey, () => false);
    await ladeVersionen(scope);
    // ⚠ Nicht „wiederhergestellt" ohne Einschränkung: die Datenbank ist zurück, die Portale nicht.
    setMessage({
      severity: 'info',
      text: `Fassung ${id} ist wieder aktiv (${res.data.length} Zeichen) — in der Datenbank. Auf die Seiten kommt sie erst mit „Ausspielen".`,
    });
  };

  // ---------------------------------------------------------------- Ausspielen

  const ausspielen = async (keys: string[]) => {
    setBusy(true);
    setMessage(null);
    const ergebnisse: Array<{ key: string; ok: boolean; error?: string }> = [];
    for (const key of keys) {
      const res = await postData<{ ok: boolean; error?: string }>(
        `/api/broker-hub/sites/${key}/deploy-base-js`,
        {},
      );
      const ok = res.success && Boolean((res.data as any)?.ok);
      ergebnisse.push({ key, ok, error: ok ? undefined : (res.data as any)?.error || res.error });
    }
    setBusy(false);
    const kaputt = ergebnisse.filter((e) => !e.ok);
    setMessage({
      severity: kaputt.length ? (kaputt.length === ergebnisse.length ? 'error' : 'warning') : 'success',
      text: kaputt.length
        ? `JS ausgespielt: ${ergebnisse.length - kaputt.length}/${ergebnisse.length}. Fehlgeschlagen: ${kaputt
            .map((e) => `${e.key.toUpperCase()} (${e.error ?? 'unbekannt'})`)
            .join(', ')}`
        : `JS ausgespielt auf ${ergebnisse.map((e) => e.key.toUpperCase()).join(', ')} — die Datei lädt auf allen Hub-Seiten dieser Portale.`,
    });
  };

  // ---------------------------------------------------------------- Darstellung

  const ladeHinweis = (ebene: Ebene, neuLaden: () => void) => {
    if (ebene.status === 'loading') {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" gap={1} p={4}>
          <CircularProgress size={20} />
          <Typography variant="body2">Bestand wird geladen …</Typography>
        </Box>
      );
    }
    if (ebene.status === 'error') {
      return (
        <Box p={3}>
          <Alert
            severity="error"
            action={
              <Button size="small" startIcon={<RefreshOutlined />} onClick={neuLaden}>
                Erneut laden
              </Button>
            }
          >
            {ebene.error} — <strong>nicht bearbeitbar</strong>, solange der Bestand unbekannt ist.
          </Alert>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box display="flex" flexDirection="column" flex={1} overflow="hidden">
      <Alert severity="info" sx={{ mb: 1 }}>
        JavaScript läuft auf allen Hub-Seiten von{' '}
        {sites.filter(hatKanal).map((s) => s.site_key.toUpperCase()).join(', ') || '—'}. Es lädt
        nach den Skripten des Themes. Code im Seiteninhalt (Blöcke, HTML-Block)
        wird weiterhin entfernt — nur was hier steht, kommt an. Jede Ebene läuft in einem eigenen{' '}
        <code>try</code>-Block: <code>let</code>/<code>const</code> auf oberster Ebene sind darin
        lokal, was eine andere Ebene sehen soll, gehört an <code>window</code>.
      </Alert>
      {message && (
        <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 1 }}>
          {message.text}
        </Alert>
      )}
      <Box display="flex" gap={1} mb={1} alignItems="center" flexWrap="wrap">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scope}
          onChange={(_e, v) => {
            if (!v) return;
            setScope(v);
            setVersions(null);
          }}
        >
          <ToggleButton value="base">Basis (alle JS-Portale)</ToggleButton>
          <ToggleButton value="override">Portal</ToggleButton>
        </ToggleButtonGroup>
        {scope === 'override' && (
          <TextField
            select
            size="small"
            label="Portal"
            value={siteKey}
            onChange={(e) => setSiteKey(e.target.value)}
            sx={{ width: 240 }}
          >
            {sites.map((s) => (
              <MenuItem key={s.site_key} value={s.site_key}>
                {s.name} ({s.site_key.toUpperCase()}){hatKanal(s) ? '' : ' — kein JS-Kanal'}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Button
          variant="contained"
          color="secondary"
          startIcon={<SaveOutlined />}
          disabled={!dirty || !bereit || (scope === 'override' && !siteKey)}
          onClick={() => speichern(scope)}
        >
          Speichern
        </Button>
        {scope === 'base' ? (
          <Tooltip title={dirty ? 'Erst speichern' : 'Basis + jeweilige Portal-Ebene auf alle JS-Portale'}>
            <span>
              <Button
                variant="outlined"
                startIcon={busy ? <CircularProgress size={16} /> : <CloudUploadOutlined />}
                disabled={dirty || !bereit || kanalSites.length === 0}
                onClick={() => ausspielen(kanalSites.map((s) => s.site_key))}
              >
                Auf alle JS-Portale ausspielen
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Tooltip
            title={
              dirty
                ? 'Erst speichern'
                : gewaehlt && !hatKanal(gewaehlt)
                  ? 'Dieses Portal hat noch keinen JS-Kanal — speichern geht, ausspielen nicht.'
                  : 'Basis + diese Ebene auf das Portal ausspielen'
            }
          >
            <span>
              <Button
                variant="outlined"
                startIcon={busy ? <CircularProgress size={16} /> : <CloudUploadOutlined />}
                disabled={dirty || !bereit || !gewaehlt || !hatKanal(gewaehlt)}
                onClick={() => ausspielen([siteKey])}
              >
                Ausspielen
              </Button>
            </span>
          </Tooltip>
        )}
        <Button size="small" startIcon={<HistoryOutlined />} onClick={() => ladeVersionen(scope)}>
          Fassungen
        </Button>
        {aktiv.status === 'ready' && (
          <Typography variant="caption" color="text.secondary">
            Bestand: {aktiv.saved.length} Zeichen
            {dirty ? ` → ${aktiv.js.length} nach dem Speichern` : ''}
          </Typography>
        )}
      </Box>
      <Box flex={1} border="1px solid #e0e0e0">
        {(scope === 'base'
          ? ladeHinweis(base, ladeBase)
          : ladeHinweis(portal, () => void ladePortal(siteKey, () => false))) ?? (
          <Editor
            height="100%"
            defaultLanguage="javascript"
            path={scope === 'base' ? 'hub-base.js' : `hub-${siteKey}.js`}
            value={aktiv.js}
            onChange={(v) =>
              (scope === 'base' ? setBase : setPortal)((p) => ({ ...p, js: v ?? '' }))
            }
            options={{ minimap: { enabled: false }, wordWrap: 'on', fontSize: 13, scrollBeyondLastLine: false }}
          />
        )}
      </Box>
      {versions !== null && (
        <Box mt={1} border="1px solid #e0e0e0" maxHeight={220} overflow="auto">
          {versions.length === 0 ? (
            <Box p={1.5}>
              <Typography variant="body2" color="text.secondary">
                Noch keine Fassungen aufgezeichnet.
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {versions.map((v) => {
                const istAktiv = v.sha256 === aktiv.sha;
                return (
                  <ListItemButton key={v.id} disableRipple sx={{ alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={
                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="body2">
                            {new Date(v.created_at).toLocaleString('de-DE')} · {v.length} Zeichen
                          </Typography>
                          {istAktiv && <Chip size="small" color="success" label="aktiv" />}
                        </Box>
                      }
                      secondary={`${v.created_by ?? 'unbekannt'}${v.change_note ? ` · ${v.change_note}` : ''}`}
                    />
                    {!istAktiv && (
                      <Button size="small" disabled={busy} onClick={() => wiederherstellen(v.id)}>
                        Zurückholen
                      </Button>
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      )}

      <Dialog open={leerFrage !== null} onClose={() => setLeerFrage(null)}>
        <DialogTitle>JS-Ebene wirklich leeren?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Der bisherige Inhalt hat <strong>{leerFrage?.bisher} Zeichen</strong>. Die Fassung bleibt
            unter „Fassungen“ abrufbar. Nach dem nächsten Ausspielen läuft der Code nicht mehr —
            sind Basis und Portal-Ebene leer, entfernt das Portal die Datei ganz (Notaus).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeerFrage(null)}>Abbrechen</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              const f = leerFrage!;
              setLeerFrage(null);
              void speichern(f.scope, true);
            }}
          >
            Leeren
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JsEbenenPanel;
